import { ordersAPI } from '~/services/api';
import { WEB_ORDERS_API } from '~/services/web-orders.service';

import { type GetOrders } from './get-orders.service.types';

/**
 * Fetches orders with flexible filtering, sorting, and pagination.
 *
 * Utilizes a set of parameters for detailed querying, suitable for dynamic order retrieval. Supports filtering by date, status, item specifics, and more, along with pagination and sorting capabilities.
 *
 * @param {GetOrdersParams} params - Parameters for query customization:
 *   - `results`: Number of orders per page.
 *   - `page`: Page number for pagination.
 *   - `destination`: Filter by shipping or delivery location.
 *   - `end_date`: Upper limit for order creation date range.
 *   - `id`: Array of specific order IDs.
 *   - `item`: Array of item identifiers for filtering.
 *   - `mass_max`, `mass_min`: Order mass range for filtering, used with `mass_unit`.
 *   - `mass_unit`: Unit of mass (e.g., kg, lbs).
 *   - `order_by`: Attribute for result ordering.
 *   - `order_status`: Filter by order status.
 *   - `shipping_method`: Array of shipping methods for filtering.
 *   - `shop_app`: Identifier for filtering by shop or app.
 *   - `sort_by`: Attribute for sorting results.
 *   - `start_date`: Lower limit for order creation date range.
 *
 * @return {Promise<AxiosResponse<GetOrdersResponseBody>>} Promise resolving to filtered, sorted, and paginated order data, including metadata like total order count. It returns an array of `WebOrders` as the `results`.
 */
export const getOrders: GetOrders = ({
  destination,
  end_date,
  hidden,
  id,
  item,
  mass_max,
  mass_min,
  mass_unit,
  order_by,
  order_status,
  page,
  results,
  shipping_method,
  shop_app,
  sort_by,
  start_date,
  tag,
  untagged,
}) =>
  ordersAPI.request({
    method: 'GET',
    params: {
      destination,
      end_date,
      hidden,
      id,
      item,
      mass_max,
      mass_min,
      mass_unit,
      order_by,
      order_status,
      page,
      results,
      shipping_method,
      shop_app,
      sort_by,
      start_date,
      tag,
      untagged,
    },
    url: `${WEB_ORDERS_API}`,
  });
